import apolloClient from '../apolloClient';
import { GET_CUSTOMER_CAMPAIGN_ART, GET_CUSTOMER_CAMPAIGN_ART_FILES } from './queries/campaignArt';
import { 
  SUBMIT_CUSTOMER_ART_BRIEF, 
  REVIEW_CUSTOMER_ART_PROOF, 
  ADD_CUSTOMER_ART_NOTE,
  UPDATE_CUSTOMER_ART_BRIEF 
} from './mutations/campaignArt';

/**
 * Fetch campaign art and art brief for customer portal
 * Includes art brief data and art proof files
 */
export const fetchCustomerCampaignArtGraphQL = async (campaignId) => {
  try {
    const { data } = await apolloClient.query({
      query: GET_CUSTOMER_CAMPAIGN_ART,
      variables: {
        campaignId: parseInt(campaignId, 10)
      },
      fetchPolicy: 'network-only' // Always fetch fresh art data
    });

    return data.customerCampaignArt || null;
  } catch (error) {
    console.error('Error fetching customer campaign art:', error);
    throw error;
  }
};

/**
 * Fetch campaign art files for customer portal
 * Returns art proof files with status and notes
 */
export const fetchCustomerCampaignArtFilesGraphQL = async (campaignArtId) => {
  try {
    const { data } = await apolloClient.query({
      query: GET_CUSTOMER_CAMPAIGN_ART_FILES,
      variables: {
        campaignArtId: parseInt(campaignArtId, 10)
      },
      fetchPolicy: 'network-only'
    });

    return data.customerCampaignArtFiles || [];
  } catch (error) {
    console.error('Error fetching customer campaign art files:', error);
    throw error;
  }
};

/**
 * Submit art design brief for customer portal
 * Creates art brief with concept files and brand information
 */
export const submitCustomerArtBriefGraphQL = async (input) => {
  try {
    const { data } = await apolloClient.mutate({
      mutation: SUBMIT_CUSTOMER_ART_BRIEF,
      variables: {
        input: {
          campaignId: parseInt(input.campaignId, 10),
          fileUrls: input.fileUrls || [],
          headlineText: input.headlineText || null,
          colors: input.colors || null,
          phone: input.phone || null,
          email: input.email || null,
          website: input.website || null,
          facebook: input.facebook || null,
          instagram: input.instagram || null,
          twitter: input.twitter || null,
          tikTok: input.tiktok || null,
          snapchat: input.snapchat || null,
          conceptFileUrl: input.conceptFileUrl || null
        }
      }
    });

    return data.submitCustomerArtBrief;
  } catch (error) {
    console.error('Error submitting customer art brief:', error);
    throw error;
  }
};

/**
 * Review (approve/reject) art proof for customer portal
 * Updates campaign status automatically based on approval
 */
export const reviewCustomerArtProofGraphQL = async (campaignArtBriefId, isApproved) => {
  try {
    const { data } = await apolloClient.mutate({
      mutation: REVIEW_CUSTOMER_ART_PROOF,
      variables: {
        campaignArtBriefId: parseInt(campaignArtBriefId, 10),
        isApproved: Boolean(isApproved)
      }
    });

    return data.reviewCustomerArtProof;
  } catch (error) {
    console.error('Error reviewing customer art proof:', error);
    throw error;
  }
};

/**
 * Add art note/comment for customer portal
 * Customer adds comment to art proof file
 * SenderType automatically set to 160 (Client)
 */
export const addCustomerArtNoteGraphQL = async (campaignArtFileId, note) => {
  try {
    const { data } = await apolloClient.mutate({
      mutation: ADD_CUSTOMER_ART_NOTE,
      variables: {
        campaignArtFileId: parseInt(campaignArtFileId, 10),
        note: note.trim()
      }
    });

    return data.addCustomerArtNote;
  } catch (error) {
    console.error('Error adding customer art note:', error);
    throw error;
  }
};

/**
 * Update art design brief for customer portal
 * Updates existing art brief with new information
 */
export const updateCustomerArtBriefGraphQL = async (input) => {
  try {
    const { data } = await apolloClient.mutate({
      mutation: UPDATE_CUSTOMER_ART_BRIEF,
      variables: {
        input: {
          campaignArtBriefId: parseInt(input.campaignArtBriefId, 10),
          campaignId: parseInt(input.campaignId, 10),
          fileUrls: input.fileUrls || null,
          headlineText: input.headlineText || null,
          colors: input.colors || null,
          phone: input.phone || null,
          email: input.email || null,
          website: input.website || null,
          facebook: input.facebook || null,
          instagram: input.instagram || null,
          twitter: input.twitter || null,
          tikTok: input.tiktok || null,
          snapchat: input.snapchat || null,
          conceptFileUrl: input.conceptFileUrl || null
        }
      }
    });

    return data.updateCustomerArtBrief;
  } catch (error) {
    console.error('Error updating customer art brief:', error);
    throw error;
  }
};

